import { useState } from 'react';
import { 
  BookOpenIcon, 
  DocumentTextIcon, 
  PlusIcon, 
  TrashIcon,
  PencilIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, Badge, Spinner } from '@realvisor/ui';
import toast from 'react-hot-toast';
import { useProject } from '@/contexts/ProjectContext';
import { useDocuments, useDeleteDocument, type Document } from '@/hooks';
import { UploadDocumentModal } from './UploadDocumentModal';
import { EditDocumentModal } from './EditDocumentModal';
import { DocumentViewerModal } from '@/components/documents/DocumentViewerModal';

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  } catch {
    return '-';
  }
}

export default function Guides() {
  const { currentProject, isAllProjects } = useProject();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [viewingDocument, setViewingDocument] = useState<Document | null>(null);
  const [selectedProjectFilter, setSelectedProjectFilter] = useState<'all' | 'general' | string>('all');

  // Načíst dokumenty podle filtru
  const projectId = selectedProjectFilter === 'all' 
    ? undefined 
    : selectedProjectFilter === 'general' 
    ? 'general' 
    : selectedProjectFilter;

  const { data: documents, isLoading } = useDocuments({ projectId });
  const deleteDocument = useDeleteDocument();

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Opravdu chcete smazat dokument "${title}"?`)) return;

    try {
      await deleteDocument.mutateAsync(id);
      toast.success('Dokument smazán');
    } catch {
      toast.error('Nepodařilo se smazat dokument');
    }
  };

  const handleView = (doc: Document) => {
    setViewingDocument(doc);
  };

  const handleDownload = (doc: Document) => {
    window.open(doc.fileUrl, '_blank');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Rozdělení dokumentů na obecné a projektové
  const generalDocs = documents?.filter(d => !d.projectId) || [];
  const projectDocs = documents?.filter(d => d.projectId) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpenIcon className="h-8 w-8 text-primary-600" />
            Návody a dokumentace
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Dokumenty a návody pro práci s leady a projekty
          </p>
        </div>
        <Button onClick={() => setUploadModalOpen(true)}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Přidat dokument
        </Button>
      </div>

      {/* Filter */}
      <Card>
        <div className="p-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Zobrazit:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedProjectFilter('all')}
                className={`px-3 py-1 text-sm rounded-lg ${
                  selectedProjectFilter === 'all'
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Všechny
              </button>
              <button
                onClick={() => setSelectedProjectFilter('general')}
                className={`px-3 py-1 text-sm rounded-lg ${
                  selectedProjectFilter === 'general'
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Obecné
              </button>
              {currentProject && !isAllProjects && (
                <button
                  onClick={() => setSelectedProjectFilter(currentProject.id)}
                  className={`px-3 py-1 text-sm rounded-lg ${
                    selectedProjectFilter === currentProject.id
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {currentProject.name}
                </button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          {/* Obecné návody */}
          {(selectedProjectFilter === 'all' || selectedProjectFilter === 'general') && generalDocs.length > 0 && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  📄 Obecné návody (pro všechny projekty)
                </h2>
                <div className="space-y-3">
                  {generalDocs.map((doc) => (
                    <DocumentItem
                      key={doc.id}
                      document={doc}
                      onEdit={setEditingDocument}
                      onDelete={handleDelete}
                      onView={handleView}
                      onDownload={handleDownload}
                      formatFileSize={formatFileSize}
                    />
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Projektové návody */}
          {(selectedProjectFilter === 'all' || (selectedProjectFilter !== 'general' && projectDocs.length > 0)) && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  📋 Návody pro projekty
                </h2>
                <div className="space-y-3">
                  {projectDocs.map((doc) => (
                    <DocumentItem
                      key={doc.id}
                      document={doc}
                      onEdit={setEditingDocument}
                      onDelete={handleDelete}
                      onView={handleView}
                      onDownload={handleDownload}
                      formatFileSize={formatFileSize}
                    />
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Empty state */}
          {documents?.length === 0 && (
            <Card>
              <div className="p-12 text-center">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">Žádné dokumenty</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Začněte přidáním prvního návodu nebo dokumentu.
                </p>
                <Button onClick={() => setUploadModalOpen(true)} className="mt-6">
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Přidat dokument
                </Button>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Modals */}
      <UploadDocumentModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
      />
      
      {editingDocument && (
        <EditDocumentModal
          document={editingDocument}
          open={!!editingDocument}
          onClose={() => setEditingDocument(null)}
        />
      )}

      <DocumentViewerModal
        document={viewingDocument}
        isOpen={!!viewingDocument}
        onClose={() => setViewingDocument(null)}
      />
    </div>
  );
}

interface DocumentItemProps {
  document: Document;
  onEdit: (doc: Document) => void;
  onDelete: (id: string, title: string) => void;
  onView: (doc: Document) => void;
  onDownload: (doc: Document) => void;
  formatFileSize: (bytes: number) => string;
}

function DocumentItem({ document, onEdit, onDelete, onView, onDownload, formatFileSize }: DocumentItemProps) {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <DocumentTextIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900 truncate">{document.title}</h3>
            {document.project && (
              <Badge
                variant="default"
                style={{ backgroundColor: `${document.project.color}20`, color: document.project.color }}
              >
                {document.project.name}
              </Badge>
            )}
          </div>
          {document.description && (
            <p className="text-sm text-gray-500 truncate">{document.description}</p>
          )}
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            <span>{document.fileName}</span>
            <span>•</span>
            <span>{formatFileSize(document.fileSize)}</span>
            <span>•</span>
            <span>{formatDate(document.createdAt)}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 ml-4">
        <button
          onClick={() => onView(document)}
          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
          title="Zobrazit dokument"
        >
          <DocumentTextIcon className="h-5 w-5" />
        </button>
        <button
          onClick={() => onDownload(document)}
          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
          title="Stáhnout"
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
        </button>
        <button
          onClick={() => onEdit(document)}
          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
          title="Upravit"
        >
          <PencilIcon className="h-5 w-5" />
        </button>
        <button
          onClick={() => onDelete(document.id, document.title)}
          className="p-2 text-gray-600 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
          title="Smazat"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
